import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, IconButton, Avatar, TablePagination
} from '@mui/material';
import {
  RecordVoiceOver, WhatsApp, History, ReceiptLong,
  Add, ShoppingCart, ArrowForward, Phone, Message, Campaign
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';

const CreditCard = ({ title, purchased, used, remaining, color, icon, onPurchase }) => {
  const usedPercentage = purchased > 0 ? (used / purchased) * 100 : 0;
  
  return (
    <Card sx={{ height: '100%', borderRadius: '16px', border: `1px solid ${color}20` }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Avatar sx={{ bgcolor: `${color}15`, color: color }}>
            {icon}
          </Avatar>
          <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem"   // same size as Recent Calls / Recent WhatsApp Messages
              }}
          >
            {title}
          </Typography>
        </Box>
        
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">Purchased</Typography>
            <Typography variant="h6" fontWeight={700}>{purchased}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">Used</Typography>
            <Typography variant="h6" fontWeight={700}>{used}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">Remaining</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color }}>{remaining}</Typography>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
            <LinearProgress 
                variant="determinate" 
                value={usedPercentage} 
                sx={{ 
                    height: 8, 
                    borderRadius: 4, 
                    bgcolor: `${color}10`,
                    '& .MuiLinearProgress-bar': { bgcolor: color }
                }} 
            />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                {usedPercentage.toFixed(0)}% Used
            </Typography>
        </Box>

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontStyle: 'italic', mb: 1 }}>
            Credits are included with your subscription. Contact support if additional credits are required.
        </Typography>
      </CardContent>
    </Card>
  );
};

const CommunicationOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [callsPage, setCallsPage] = useState(0);
  const [callsRowsPerPage, setCallsRowsPerPage] = useState(5);

  const [whatsappPage, setWhatsappPage] = useState(0);
  const [whatsappRowsPerPage, setWhatsappRowsPerPage] = useState(5);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/applications/comm-center/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  const recentCalls = stats?.recent?.filter(r => r.type === 'voice') || [];
  const recentWhatsApp = stats?.recent?.filter(r => r.type === 'whatsapp') || [];

  const paginatedCalls = recentCalls.slice(
      callsPage * callsRowsPerPage,
      callsPage * callsRowsPerPage + callsRowsPerPage
  );

  const paginatedWhatsApp = recentWhatsApp.slice(
      whatsappPage * whatsappRowsPerPage,
      whatsappPage * whatsappRowsPerPage + whatsappRowsPerPage
  );

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Communication Center
                </Typography>
        <Typography variant="body1" color="text.secondary">Manage AI Calling, WhatsApp messages, templates and credits</Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <CreditCard 
            title="Calling Credits" 
            purchased={stats?.calling?.purchased || 0}
            used={stats?.calling?.used || 0}
            remaining={stats?.calling?.remaining || 0}
            color="#10B981"
            icon={<Phone />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CreditCard 
            title="WhatsApp Credits" 
            purchased={stats?.whatsapp?.purchased || 0}
            used={stats?.whatsapp?.used || 0}
            remaining={stats?.whatsapp?.remaining || 0}
            color="#2563EB"
            icon={<WhatsApp />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: '16px', background: 'linear-gradient(135deg, #FFF 0%, #F8FAFC 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Avatar sx={{ bgcolor: '#F59E0B15', color: '#F59E0B' }}>
                  <ReceiptLong />
                </Avatar>
                <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.25rem"
                    }}
                >
                  Balance Summary
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Total Remaining Credits</Typography>
              <Typography variant="h3" fontWeight={800} sx={{ mb: 2 }}>
                {(stats?.calling?.remaining || 0) + (stats?.whatsapp?.remaining || 0)}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip label={`Calling: ${stats?.calling?.remaining || 0}`} size="small" sx={{ bgcolor: '#10B98115', color: '#10B981', fontWeight: 700 }} />
                <Chip label={`WhatsApp: ${stats?.whatsapp?.remaining || 0}`} size="small" sx={{ bgcolor: '#2563EB15', color: '#2563EB', fontWeight: 700 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" fontWeight={700}>Recent Calls</Typography>
            <Button endIcon={<ArrowForward />} onClick={() => navigate('/communication-center/history?type=voice')}>View All</Button>
          </Box>
          <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: 'none', border: '1px solid #E2E8F0' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Candidate</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Template</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCalls.length > 0 ? paginatedCalls.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: 'primary.main' }}>
                                {row.first_name?.[0]}{row.last_name?.[0]}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>{row.first_name} {row.last_name}</Typography>
                        </Box>
                    </TableCell>
                    <TableCell>{row.template_name || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={row.status} 
                        size="small" 
                        color={row.status === 'completed' ? 'success' : row.status === 'failed' ? 'error' : 'warning'} 
                        sx={{ fontWeight: 600, textTransform: 'capitalize', fontSize: '0.65rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell>{row.duration ? `${row.duration}s` : '-'}</TableCell>
                    <TableCell>
                        <Typography variant="caption">{new Date(row.created_at).toLocaleDateString()}</Typography>
                    </TableCell>
                  </TableRow>
                )) : (
                    <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">No calls sent yet</Typography>
                        </TableCell>
                    </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
                component="div"
                count={recentCalls.length}
                page={callsPage}
                onPageChange={(event, newPage) => setCallsPage(newPage)}
                rowsPerPage={callsRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setCallsRowsPerPage(parseInt(event.target.value, 10));
                  setCallsPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage=""
                labelDisplayedRows={({ from, to, count }) =>
                    `Showing ${from} to ${to} of ${count} Calls`
                }
            />
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" fontWeight={700}>Recent WhatsApp Messages</Typography>
            <Button endIcon={<ArrowForward />} onClick={() => navigate('/communication-center/history?type=whatsapp')}>View All</Button>
          </Box>
          <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: 'none', border: '1px solid #E2E8F0' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Candidate</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Template</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedWhatsApp.length > 0 ? paginatedWhatsApp.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: 'primary.main' }}>
                                {row.first_name?.[0]}{row.last_name?.[0]}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>{row.first_name} {row.last_name}</Typography>
                        </Box>
                    </TableCell>
                    <TableCell>{row.template_name || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={row.status} 
                        size="small" 
                        color={row.status === 'delivered' ? 'success' : row.status === 'failed' ? 'error' : 'warning'} 
                        sx={{ fontWeight: 600, textTransform: 'capitalize', fontSize: '0.65rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                        <Typography variant="caption">{new Date(row.created_at).toLocaleDateString()}</Typography>
                    </TableCell>
                  </TableRow>
                )) : (
                    <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">No messages sent yet</Typography>
                        </TableCell>
                    </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
                component="div"
                count={recentWhatsApp.length}
                page={whatsappPage}
                onPageChange={(event, newPage) => setWhatsappPage(newPage)}
                rowsPerPage={whatsappRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setWhatsappRowsPerPage(parseInt(event.target.value, 10));
                  setWhatsappPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage=""
                labelDisplayedRows={({ from, to, count }) =>
                    `Showing ${from} to ${to} of ${count} Messages`
                }
            />
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CommunicationOverview;
